import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import { canUseChat, isAdminRole } from '../auth/roles';
import { useSession } from '../auth/session';
import { titleForPath } from '../app/screens';
import { AskDocsight } from '../screens/chatbot';
import { REVIEW_CHANGED_EVENT } from '../screens/review';
import { useMyBrand } from '../theme/useMyBrand';

import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';

import './shell.css';

export interface AppShellProps {
  /** Screen body. Defaults to the routed `<Outlet />`. */
  children?: ReactNode;
  /** Override the review-queue badge (else fetched from the API). */
  reviewCount?: number;
  /**
   * Override the tenant name in the header. Omit it — as the routed shell does
   * — and the name comes from the session, which is where it lives for a real
   * signed-in user.
   */
  tenantName?: string | null;
  /** Raise the dead-letter indicator on Processing health. */
  deadLetterAlert?: boolean;
  /** Override whether Settings shows *Desktop uploader* (else asked of the API). */
  hasDesktopUploader?: boolean;
}

/**
 * The Docsight application frame: a 216px left nav, a 56px top bar and the
 * scrolling content region. Role-aware — the ADMIN nav group and the tenant
 * switcher come from the session — and it fetches its own review badge from the
 * typed API. The notifications bell owns its own data. Everything except
 * `/login` renders inside it.
 */
export function AppShell({
  children,
  reviewCount,
  tenantName,
  deadLetterAlert = false,
  hasDesktopUploader,
}: AppShellProps): React.JSX.Element {
  const { pathname } = useLocation();
  const { role, api, status, principal } = useSession();
  // "Resolved by hostname at runtime" (Settings → Branding's own promise)
  // applies to the whole signed-in app too, not just the pre-auth sign-in
  // card — otherwise a tenant's accent/name only ever look right for as long
  // as the tab that clicked Apply stays open, and revert on the next refresh.
  const { brand, logoUrl } = useMyBrand();

  // The routed shell passes no name, so the session is the real source: the
  // signed-in user belongs to exactly one tenant and `/v1/auth/me` names it.
  // An explicit prop still wins, which is what the tests and any embedded use
  // of the shell rely on.
  const activeTenantName = tenantName !== undefined ? tenantName : (principal?.tenant_name ?? null);

  const [fetchedReview, setFetchedReview] = useState(0);
  const [fetchedDlq, setFetchedDlq] = useState(false);
  // Fails closed: until the answer arrives — or if it never does — the Settings
  // sub-nav simply has no Desktop uploader row, which is the state that is true
  // for most tenants most of the time anyway.
  const [fetchedDesktop, setFetchedDesktop] = useState(false);

  const isAdmin = isAdminRole(role);
  const showsSettings = role === 'tenant_admin';

  const refreshReviewCount = useCallback(async () => {
    try {
      setFetchedReview(await api.review.openCount());
    } catch {
      /* leave the badge as-is if the refresh fails */
    }
  }, [api]);

  // Keep the left-nav Review badge live: the review screens broadcast
  // REVIEW_CHANGED_EVENT after every resolve / bulk-resolve / create-category /
  // sweep, so the count reflects the queue without a full reload.
  useEffect(() => {
    if (status !== 'authenticated') return undefined;
    const handler = (): void => {
      void refreshReviewCount();
    };
    window.addEventListener(REVIEW_CHANGED_EVENT, handler);
    return () => {
      window.removeEventListener(REVIEW_CHANGED_EVENT, handler);
    };
  }, [status, refreshReviewCount]);

  useEffect(() => {
    if (status !== 'authenticated') return;
    const live = { current: true };
    void (async () => {
      try {
        const open = await api.review.openCount();
        if (live.current) setFetchedReview(open);
      } catch {
        /* leave the badge hidden */
      }
      if (isAdmin) {
        try {
          const health = await api.admin.health();
          if (live.current) setFetchedDlq(health.total_dead_lettered > 0);
        } catch {
          /* leave the DLQ dot hidden */
        }
      }
      // Only a tenant_admin can open a Settings screen at all, so nobody else
      // needs the answer and nobody else pays for the request.
      if (showsSettings) {
        try {
          const release = await api.settings.desktopRelease();
          if (live.current) setFetchedDesktop(release !== null);
        } catch {
          /* leave the Desktop uploader row off */
        }
      }
    })();
    return () => {
      live.current = false;
    };
  }, [api, status, isAdmin, showsSettings]);

  return (
    <div className="shell">
      <a className="skip-link" href="#main-content">
        Skip to main content
      </a>
      <Sidebar
        reviewCount={reviewCount ?? fetchedReview}
        deadLetterAlert={deadLetterAlert || fetchedDlq}
        role={role}
        brand={brand}
        logoUrl={logoUrl}
        hasDesktopUploader={hasDesktopUploader ?? fetchedDesktop}
      />
      <div className="shell-main">
        <TopBar title={titleForPath(pathname)} role={role} tenantName={activeTenantName} />
        <main id="main-content" className="shell-content" tabIndex={-1}>
          {children ?? <Outlet />}
        </main>
      </div>
      {/* The floating "Ask Docsight" button + docked chat panel. An overlay
          (position:fixed), not a route; it listens for the docsight:open-chat
          event the Dashboard/Automation dispatch, and sits inside the Router +
          Session + Toast providers the shell already establishes. */}
      {canUseChat(role) ? <AskDocsight brand={brand} logoUrl={logoUrl} /> : null}
    </div>
  );
}
